import { ChevronLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import { fetchMemberById } from '../api/membersDirectory';
import {
    MemberDetailContent,
    MemberDetailPageSkeleton,
} from '../components/MemberDetailContent';
import { PageSeo } from '../components/PageSeo';
import { SubpageLayout } from '../components/SubpageLayout';
import { resolvePublicMediaUrl } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { findClubMemberForDetailId } from '../data/members';
import { mapClubMemberToDetailView } from '../utils/memberDetailView';
import { truncateMetaDescription } from '../utils/metaText';

/**
 * Üye profil detayı — `/member-area/members/:memberId`
 * Canlı oturumda `GET .../members/api/Member/{id}`; simülasyonda örnek `MEMBER_BY_ID`.
 */
export function MemberDetailPage() {
    const { t } = useTranslation();
    const { memberId } = useParams();
    const location = useLocation();
    const { authToken, isSimulatedAuth } = useAuth();
    const [member, setMember] = useState(
        /** @type {ReturnType<typeof mapClubMemberToDetailView> | null} */ (
            null
        ),
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(/** @type {string | null} */ (null));

    useEffect(() => {
        let cancelled = false;

        (async () => {
            if (!memberId) {
                setError('Geçersiz adres');
                setLoading(false);
                setMember(null);
                return;
            }

            setLoading(true);
            setError(null);
            setMember(null);

            try {
                if (isSimulatedAuth) {
                    const resolved = findClubMemberForDetailId(memberId);
                    if (!resolved) {
                        throw new Error(
                            'Örnek üye listesinde bu kayıt bulunamadı.',
                        );
                    }
                    if (cancelled) return;
                    setMember(
                        mapClubMemberToDetailView(resolved.record, {
                            viewAsPartner: resolved.viewAsPartner,
                        }),
                    );
                } else {
                    if (!authToken) {
                        throw new Error('Oturum gerekli');
                    }
                    const data = await fetchMemberById(authToken, memberId);
                    if (cancelled) return;
                    setMember(data);
                }
            } catch (e) {
                if (cancelled) return;
                setError(
                    e instanceof Error ? e.message : 'Üye yüklenemedi',
                );
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [memberId, authToken, isSimulatedAuth]);

    if (loading) {
        return (
            <>
                <PageSeo
                    title={`${t('membersPage.title')} — ${t('header.rotaryAlt')}`}
                    description={t('seo.membersDirectory')}
                    noindex
                />
                <SubpageLayout
                    title={t('memberDetailPage.title')}
                    contentClassName="member-detail-page"
                >
                    <MemberDetailPageSkeleton />
                </SubpageLayout>
            </>
        );
    }

    if (error || !member) {
        const navigationState =
            location.state && typeof location.state === 'object'
                ? /** @type {{ fromProfile?: boolean, fromFellowshipGroups?: boolean }} */ (
                      location.state
                  )
                : {};
        const errorBackLink = navigationState.fromProfile
            ? {
                  to: '/profile',
                  label: t('memberDetailPage.backToProfile'),
              }
            : navigationState.fromFellowshipGroups
              ? {
                    to: '/member-area/fellowship-groups',
                    label: t('memberArea.nav.ocakbasi'),
                }
              : {
                    to: '/member-area/members',
                    label: t('memberDetailPage.backToListShort'),
                };

        return (
            <>
                <PageSeo
                    title={`${t('membersPage.title')} — ${t('header.rotaryAlt')}`}
                    description={t('seo.memberDetailFallback')}
                    noindex
                />
                <SubpageLayout
                    title={t('memberDetailPage.title')}
                    contentClassName="member-detail-page"
                >
                    <p className="member-detail-placeholder mb-3 text-secondary">
                        {error || t('memberDetailPage.notFoundSubtitle')}
                    </p>
                    <p className="member-detail-placeholder mb-4">
                        <Link
                            to={errorBackLink.to}
                            className="member-detail-back-link d-inline-flex align-items-center gap-2 text-decoration-none"
                        >
                            <ChevronLeft size={20} strokeWidth={2} aria-hidden />
                            {errorBackLink.label}
                        </Link>
                    </p>
                </SubpageLayout>
            </>
        );
    }

    const nameForTitle = member.fullName?.trim() || '—';
    const memberMetaDesc = truncateMetaDescription(
        [member.jobTitle, member.company].filter(Boolean).join(' · ') ||
            t('seo.memberDetailFallback'),
    );
    const navigationState =
        location.state && typeof location.state === 'object'
            ? /** @type {{ fromProfile?: boolean, fromFellowshipGroups?: boolean }} */ (
                  location.state
              )
            : {};
    const fromProfile = Boolean(navigationState.fromProfile);
    const fromFellowshipGroups = Boolean(navigationState.fromFellowshipGroups);
    const backLink = fromProfile
        ? {
              to: '/profile',
              label: t('memberDetailPage.backToProfile'),
          }
        : fromFellowshipGroups
          ? {
                to: '/member-area/fellowship-groups',
                label: t('memberArea.nav.ocakbasi'),
            }
          : {
                to: '/member-area/members',
                label: t('memberDetailPage.backToList'),
            };
    const memberLinkState = fromProfile
        ? { fromProfile: true }
        : fromFellowshipGroups
          ? { fromFellowshipGroups: true }
          : undefined;

    return (
        <>
            <PageSeo
                title={`${nameForTitle} — ${t('membersPage.title')}`}
                description={memberMetaDesc}
                noindex
                ogImage={
                    member.profilePictureUrl
                        ? resolvePublicMediaUrl(member.profilePictureUrl)
                        : undefined
                }
            />
            <SubpageLayout
                title={t('memberDetailPage.title')}
                contentClassName="member-detail-page"
            >
                <MemberDetailContent
                    member={member}
                    backLink={backLink}
                    memberLinkState={memberLinkState}
                />
            </SubpageLayout>
        </>
    );
}
